import { useEffect, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { colors, css } from '../game/colors';
import { getSettings, onSettings, updateSettings, type Settings } from '../game/settings';
import { AVAILABLE_LANGS, LANG_NAMES, REVIEWED_LANGS, getLang, setLang, t, type Lang } from '../i18n';
import { Icon } from './icons';
import { PageHead } from './MenuShell';
import { Switch } from './ModePicker';

const subscribe = (fn: () => void) => onSettings(fn);

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('close')}>
            <Icon name="close" />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

type Toggle = 'shake' | 'reducedMotion' | 'autofire' | 'joyFixed' | 'muteHidden' | 'highContrast' | 'visualCues';
type Slider = 'volume' | 'sfxVolume' | 'uiVolume' | 'joyOpacity' | 'joySensitivity' | 'deadzone' | 'quality';

/** Ligne de réglage : libellé, commande, aide facultative. */
function Row({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="set-row">
      <label htmlFor={id} className="set-label">
        {label}
        {hint && <small>{hint}</small>}
      </label>
      <div className="set-control">{children}</div>
    </div>
  );
}

/** Paramètres : langue, son, accessibilité, affichage, commandes tactiles. `bare` : sans en-tête (feuille en partie). */
export function SettingsPanel({ bare }: { bare?: boolean } = {}) {
  const s = useSyncExternalStore(subscribe, getSettings);
  const set = (p: Partial<Settings>) => updateSettings(p);
  const sw = (k: Toggle) => <Switch id={`set-${k}`} on={s[k]} onChange={(v) => set({ [k]: v })} />;
  const slider = (k: Slider, min: number, max: number, step: number, label: string, fmt: (v: number) => string) => (
    <div className="slider">
      <input
        id={`set-${k}`}
        type="range"
        min={min}
        max={max}
        step={step}
        value={s[k]}
        aria-label={label}
        aria-valuetext={fmt(s[k])}
        style={{ '--fill': `${((s[k] - min) / (max - min)) * 100}%` } as CSSProperties}
        onChange={(e) => set({ [k]: Number(e.target.value) })}
      />
      <output className="num">{fmt(s[k])}</output>
    </div>
  );
  const pct = (v: number) => `${Math.round(v * 100)} %`;
  const seg = <K extends 'colorMode' | 'particles'>(k: K, opts: { v: Settings[K]; label: string }[], label: string) => (
    <div className="seg" role="group" aria-label={label} id={`set-${k}`}>
      {opts.map((o) => (
        <button key={String(o.v)} type="button" aria-pressed={s[k] === o.v} onClick={() => set({ [k]: o.v } as Partial<Settings>)}>
          {o.label}
        </button>
      ))}
    </div>
  );
  const pal = colors();
  return (
    <div className={`page settings-page ${bare ? 'bare' : ''}`}>
      {!bare && <PageHead title={t('settings')} sub={t('settingsSub')} />}
      <div className="settings-layout">
        <section className="plate settings-group" aria-labelledby="set-lang-title">
          <h2 className="plate-title" id="set-lang-title">
            {t('language')}
          </h2>
          <Row id="set-lang" label={t('language')}>
            <select
              id="set-lang"
              className="field"
              value={getLang()}
              onChange={(e) => {
                const l = e.target.value as Lang;
                set({ lang: l });
                void setLang(l);
              }}
            >
              {AVAILABLE_LANGS.map((l) => (
                <option key={l} value={l}>
                  {LANG_NAMES[l]}
                  {REVIEWED_LANGS.includes(l) ? '' : ' *'}
                </option>
              ))}
            </select>
          </Row>
          {!REVIEWED_LANGS.includes(getLang()) && <p className="wip settings-note">* {t('translationReview')}</p>}
        </section>

        <section className="plate settings-group" aria-labelledby="set-audio-title">
          <h2 className="plate-title" id="set-audio-title">
            {t('settingsSoundTitle')}
          </h2>
          <Row id="set-volume" label={t('settingsAudio')}>
            {slider('volume', 0, 1, 0.05, t('settingsAudio'), pct)}
          </Row>
          <Row id="set-sfxVolume" label={t('settingsSfx')}>
            {slider('sfxVolume', 0, 1, 0.05, t('settingsSfx'), pct)}
          </Row>
          <Row id="set-uiVolume" label={t('settingsUiSounds')}>
            {slider('uiVolume', 0, 1, 0.05, t('settingsUiSounds'), pct)}
          </Row>
          <Row id="set-muteHidden" label={t('settingsMuteHidden')}>
            {sw('muteHidden')}
          </Row>
        </section>

        <section className="plate settings-group" aria-labelledby="set-a11y-title">
          <h2 className="plate-title" id="set-a11y-title">
            {t('settingsA11y')}
          </h2>
          <Row id="set-colorMode" label={t('settingsColors')}>
            {seg(
              'colorMode',
              [
                { v: 'standard', label: t('colorsStandard') },
                { v: 'redgreen', label: t('colorsRedGreen') },
                { v: 'blueyellow', label: t('colorsBlueYellow') },
              ],
              t('settingsColors'),
            )}
          </Row>
          <div className="color-preview" aria-hidden>
            <span style={{ '--c': css(pal.self) } as CSSProperties}>{t('colorSelf')}</span>
            <span style={{ '--c': css(pal.ally) } as CSSProperties}>{t('colorAlly')}</span>
            <span style={{ '--c': css(pal.enemy) } as CSSProperties}>{t('colorEnemy')}</span>
            {['I', 'II', 'III', 'BOSS'].map((z, i) => (
              <span key={z} className="zone" style={{ '--c': css(pal.zoneRim[i + 1]) } as CSSProperties}>
                {z}
              </span>
            ))}
          </div>
          <Row id="set-uiScale" label={t('settingsTextSize')}>
            <div className="seg" role="group" aria-label={t('settingsTextSize')} id="set-uiScale">
              {[1, 1.15, 1.3].map((v) => (
                <button key={v} type="button" aria-pressed={s.uiScale === v} onClick={() => set({ uiScale: v })}>
                  {Math.round(v * 100)} %
                </button>
              ))}
            </div>
          </Row>
          <Row id="set-highContrast" label={t('settingsContrast')}>
            {sw('highContrast')}
          </Row>
          <Row id="set-visualCues" label={t('settingsVisualCues')} hint={t('settingsVisualCuesHint')}>
            {sw('visualCues')}
          </Row>
          <Row id="set-reducedMotion" label={t('settingsReducedMotion')} hint={t('settingsReducedMotionHint')}>
            {sw('reducedMotion')}
          </Row>
          <Row id="set-shake" label={t('settingsShake')}>
            {sw('shake')}
          </Row>
        </section>

        <section className="plate settings-group" aria-labelledby="set-display-title">
          <h2 className="plate-title" id="set-display-title">
            {t('settingsDisplayTitle')}
          </h2>
          <Row id="set-particles" label={t('settingsParticles')}>
            {seg(
              'particles',
              [
                { v: 'low', label: t('settingsLow') },
                { v: 'medium', label: t('settingsMedium') },
                { v: 'high', label: t('settingsHigh') },
              ],
              t('settingsParticles'),
            )}
          </Row>
          <Row id="set-quality" label={t('settingsQuality')} hint={t('settingsQualityHint')}>
            {slider('quality', 0.5, 1, 0.25, t('settingsQuality'), pct)}
          </Row>
        </section>

        <section className="plate settings-group" aria-labelledby="set-controls-title">
          <h2 className="plate-title" id="set-controls-title">
            {t('settingsControlsTitle')}
          </h2>
          <Row id="set-autofire" label={t('settingsAutofire')}>
            {sw('autofire')}
          </Row>
          <Row id="set-joyOpacity" label={t('settingsJoyOpacity')}>
            {slider('joyOpacity', 0.15, 1, 0.05, t('settingsJoyOpacity'), pct)}
          </Row>
          <Row id="set-joySensitivity" label={t('settingsJoySensitivity')}>
            {slider('joySensitivity', 0.5, 2, 0.1, t('settingsJoySensitivity'), (v) => `×${v.toFixed(1)}`)}
          </Row>
          <Row id="set-deadzone" label={t('settingsDeadzone')}>
            {slider('deadzone', 0.05, 0.4, 0.01, t('settingsDeadzone'), pct)}
          </Row>
          <Row id="set-joyFixed" label={t('settingsJoyFixed')}>
            {sw('joyFixed')}
          </Row>
        </section>
      </div>
    </div>
  );
}

const Key = ({ k, wide }: { k: string; wide?: boolean }) => <kbd className={`key ${wide ? 'wide' : ''}`}>{k}</kbd>;

/** Touches de déplacement affichées selon la disposition réelle du clavier (positions physiques W, A, S, D). */
function useMoveKeys(): string[] {
  const [keys, setKeys] = useState(getLang() === 'fr' ? ['Z', 'Q', 'S', 'D'] : ['W', 'A', 'S', 'D']);
  useEffect(() => {
    const kb = (navigator as Navigator & { keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> } }).keyboard;
    kb?.getLayoutMap?.()
      .then((m) => {
        const k = ['KeyW', 'KeyA', 'KeyS', 'KeyD'].map((c) => (m.get(c) ?? '').toUpperCase());
        if (k.every(Boolean)) setKeys(k);
      })
      .catch(() => {});
  }, []);
  return keys;
}

/** Aide : commandes PC et tactiles illustrées, zones de la carte, règles de loyauté. */
export function HelpPanel({ bare }: { bare?: boolean } = {}) {
  const pal = colors();
  const [up, left, down, right] = useMoveKeys();
  return (
    <div className={`page help-page ${bare ? 'bare' : ''}`}>
      {!bare && <PageHead title={t('help')} sub={t('helpSub')} />}
      <div className="help-grid">
        <section className="plate help-card" aria-labelledby="help-pc">
          <h2 className="plate-title" id="help-pc">
            {t('helpPcTitle')}
          </h2>
          <div className="keys-figure" aria-hidden>
            <div className="keys-block">
              <div className="keys-row">
                <Key k={up} />
              </div>
              <div className="keys-row">
                <Key k={left} />
                <Key k={down} />
                <Key k={right} />
              </div>
              <span className="keys-cap">{t('helpMove')}</span>
            </div>
            <div className="keys-block">
              <span className="mouse">
                <span className="mouse-btn" />
              </span>
              <span className="keys-cap">{t('helpAimFire')}</span>
            </div>
            <div className="keys-block">
              <div className="keys-row">
                <Key k="E" />
              </div>
              <span className="keys-cap">{t('autofire')}</span>
            </div>
            <div className="keys-block">
              <div className="keys-row">
                <Key k="1" />
                <Key k="…" />
                <Key k="8" />
              </div>
              <span className="keys-cap">{t('stats')}</span>
            </div>
          </div>
          <p>{t('helpPc')}</p>
        </section>

        <section className="plate help-card" aria-labelledby="help-touch">
          <h2 className="plate-title" id="help-touch">
            {t('helpTouchTitle')}
          </h2>
          <div className="touch-figure" aria-hidden>
            <span className="phone">
              <span className="stick left">
                <span className="knob" />
              </span>
              <span className="stick right">
                <span className="knob" />
              </span>
            </span>
            <span className="keys-cap">{t('helpSticks')}</span>
          </div>
          <p>{t('helpMobile')}</p>
          <p className="wip">{t('landscapeHint')}</p>
        </section>

        <section className="plate help-card" aria-labelledby="help-zones">
          <h2 className="plate-title" id="help-zones">
            {t('helpZonesTitle')}
          </h2>
          <div className="zones-figure" aria-hidden>
            {['I', 'II', 'III', 'BOSS'].map((z, i) => (
              <span key={z} style={{ '--c': css(pal.zoneRim[i + 1]) } as CSSProperties}>
                {z}
              </span>
            ))}
          </div>
          <p>{t('zoneLegend')}</p>
        </section>

        <section className="plate help-card" aria-labelledby="help-fair">
          <h2 className="plate-title" id="help-fair">
            {t('helpFairTitle')}
          </h2>
          <ul className="help-list">
            <li>
              <span className="badge-bot">{t('bot')}</span> {t('helpBots')}
            </li>
            <li>{t('helpFairView')}</li>
          </ul>
        </section>
      </div>
    </div>
  );
}
